/**
 * Supabase Call Performance Logger
 * 
 * Tracks Supabase/RPC calls during performance events like "freeworld_open" or "freeworld_submit".
 * Features:
 * - Event stack for nested events
 * - Duplicate call detection with count
 * - Sentry span integration (when available)
 * - Console.table output with top-10 slowest calls
 * 
 * Enable via:
 * - VITE_PERF_LOGS=1 env var
 * - URL param ?perf=1
 * - window.__PERF_LOGS__ = true in console
 */

import * as Sentry from '@sentry/react';

interface CallLogEntry {
  event: string;
  op: string;
  label: string;
  tableOrRpc: string;
  ms: number;
  ok: boolean;
  statusLike: string;
  rowCountApprox: number | null;
  duplicate: boolean;
  count: number;
  timestamp: number;
}

interface EventContext {
  name: string;
  startTime: number;
  callLog: CallLogEntry[];
  labelCounts: Map<string, number>;
}

// Event stack for nested events
let eventStack: EventContext[] = [];
let hasLoggedEnabled = false;

// Declare global type for window
declare global {
  interface Window {
    __PERF_LOGS__?: boolean;
  }
}

/**
 * Check if performance logging is enabled
 * Works in both dev and prod
 */
export const isPerfLoggingEnabled = (): boolean => {
  return (
    import.meta.env.VITE_PERF_LOGS === '1' ||
    (typeof window !== 'undefined' && window.__PERF_LOGS__ === true)
  );
};

/**
 * Initialize perf logging from URL param
 * Call this in main.tsx or App bootstrap
 */
export const initPerfFromUrl = (): void => {
  if (typeof window === 'undefined') return;
  
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('perf') === '1') {
    window.__PERF_LOGS__ = true;
    console.log('[perf] enabled via URL param ?perf=1');
  }
};

/**
 * Get the current active event context
 */
const getCurrentContext = (): EventContext | null => {
  return eventStack.length > 0 ? eventStack[eventStack.length - 1] : null;
};

/**
 * Start a performance event (e.g., "freeworld_open", "freeworld_submit")
 * Pushes to event stack, allowing nested events
 */
export const startPerfEvent = (name: string): void => {
  if (!isPerfLoggingEnabled()) return;
  
  // Log enabled message once
  if (!hasLoggedEnabled) {
    console.log('[perf] enabled');
    hasLoggedEnabled = true;
  }
  
  const context: EventContext = {
    name,
    startTime: performance.now(),
    callLog: [],
    labelCounts: new Map()
  };
  
  eventStack.push(context);
  console.log(`[perf] 🚀 Started event: ${name} (stack depth: ${eventStack.length})`);
};

/**
 * End the current performance event
 * Pops from event stack and prints summary
 */
export const endPerfEvent = (): void => {
  if (!isPerfLoggingEnabled()) return;
  
  const context = eventStack.pop();
  if (!context) {
    console.warn('[perf] endPerfEvent called but no active event');
    return;
  }
  
  const totalDuration = performance.now() - context.startTime;
  const { name, callLog } = context;
  
  console.log(`[perf] ✅ Ended event: ${name} (${totalDuration.toFixed(1)}ms total)`);
  
  if (callLog.length > 0) {
    // Sort by duration desc
    const sorted = [...callLog].sort((a, b) => b.ms - a.ms);
    const top10 = sorted.slice(0, 10);
    
    // Format for console.table
    const tableData = top10.map(entry => ({
      label: entry.label,
      ms: `${entry.ms.toFixed(1)}ms`,
      ok: entry.ok ? '✓' : '✗',
      count: entry.count,
      duplicate: entry.duplicate ? '⚠️ DUPE' : '',
      rows: entry.rowCountApprox ?? '-'
    }));
    
    console.table(tableData);
    
    // Summary stats
    const totalMs = callLog.reduce((sum, e) => sum + e.ms, 0);
    const dupeCount = callLog.filter(e => e.duplicate).length;
    
    console.log(
      `[perf] event ${name} total=${totalMs.toFixed(0)}ms calls=${callLog.length} duplicates=${dupeCount}`
    );
    
    // Top 3 slowest
    console.log(`[perf] 📊 Top 3 slowest:`);
    sorted.slice(0, 3).forEach((entry, i) => {
      console.log(`  ${i + 1}. ${entry.label}: ${entry.ms.toFixed(1)}ms${entry.duplicate ? ' ⚠️DUPE' : ''}`);
    });
  } else {
    console.log(`[perf] No Supabase calls tracked for "${name}"`);
  }
};

/**
 * Extract table/RPC name from label for categorization
 */
const extractTableOrRpc = (label: string): string => {
  const rpcMatch = label.match(/rpc[:\s_-]?(\w+)/i);
  if (rpcMatch) return `rpc:${rpcMatch[1]}`;
  
  const tableMatch = label.match(/^(\w+)[:\s_-]?(select|insert|update|delete)?/i);
  if (tableMatch) return tableMatch[0];
  
  return label.split(/[:\s_-]/)[0] || label;
};

/**
 * Wrap a Supabase call to track performance
 * Creates Sentry spans when SDK is available
 */
export async function wrapSupabase<T>(
  label: string,
  fn: () => Promise<T>
): Promise<T> {
  // If logging disabled, just run the function
  if (!isPerfLoggingEnabled()) {
    return fn();
  }
  
  const context = getCurrentContext();
  const eventName = context?.name || '__no_event__';
  const start = performance.now();
  
  // Track duplicates per event
  let currentCount = 1;
  let isDuplicate = false;
  
  if (context) {
    const prevCount = context.labelCounts.get(label) || 0;
    currentCount = prevCount + 1;
    context.labelCounts.set(label, currentCount);
    isDuplicate = currentCount > 1;
  }
  
  // Create Sentry span if available (don't fail if Sentry not init)
  let sentrySpan: ReturnType<typeof Sentry.startInactiveSpan> | null = null;
  try {
    sentrySpan = Sentry.startInactiveSpan({
      name: label,
      op: 'db.supabase',
      attributes: {
        'supabase.event': eventName,
        'supabase.duplicate': isDuplicate,
        'supabase.count': currentCount,
      }
    });
  } catch {
    // Sentry not available or span creation failed - that's fine
  }
  
  let ok = true;
  let statusLike = 'ok';
  let rowCountApprox: number | null = null;
  
  try {
    const result = await fn();
    
    // Try to extract info from result
    if (result && typeof result === 'object') {
      const r = result as any;
      
      if ('error' in r && r.error) {
        ok = false;
        statusLike = r.error.code || r.error.message || 'error';
      }
      
      if ('data' in r) {
        if (Array.isArray(r.data)) {
          rowCountApprox = r.data.length;
        } else if (r.data !== null) {
          rowCountApprox = 1;
        }
      }
      
      if (Array.isArray(result)) {
        rowCountApprox = result.length;
      }
    }
    
    return result;
  } catch (error) {
    ok = false;
    statusLike = error instanceof Error ? error.message.slice(0, 50) : 'exception';
    throw error;
  } finally {
    const duration = performance.now() - start;
    
    // End Sentry span
    if (sentrySpan) {
      try {
        sentrySpan.setAttribute('duration_ms', duration);
        sentrySpan.end();
      } catch {
        // Ignore span errors
      }
    }
    
    // Log entry
    const entry: CallLogEntry = {
      event: eventName,
      op: 'db.supabase',
      label,
      tableOrRpc: extractTableOrRpc(label),
      ms: duration,
      ok,
      statusLike,
      rowCountApprox,
      duplicate: isDuplicate,
      count: currentCount,
      timestamp: Date.now()
    };
    
    if (context) {
      context.callLog.push(entry);
    }
    
    // Real-time console output
    const dupeMarker = isDuplicate ? ` ⚠️ DUPE(${currentCount})` : '';
    const okMarker = ok ? '✓' : '✗';
    console.log(`[perf] ${okMarker} ${label}: ${duration.toFixed(1)}ms${dupeMarker}`);
  }
}

/**
 * Get current call log (for testing/debugging)
 */
export const getCallLog = (): readonly CallLogEntry[] => {
  const context = getCurrentContext();
  return context ? [...context.callLog] : [];
};

/**
 * Get active event name
 */
export const getActiveEvent = (): string | null => {
  const context = getCurrentContext();
  return context?.name || null;
};

/**
 * Check if currently tracking an event
 */
export const isTracking = (): boolean => {
  return eventStack.length > 0;
};

/**
 * Get event stack depth
 */
export const getEventStackDepth = (): number => {
  return eventStack.length;
};
